import MaterialIcon from '@components/common/MaterialIcon';
import ButtonT from '@components/tailwind-components/Button/ButtonT';
import RadioInputT from '@components/tailwind-components/Input/RadioInputT';
import TextareaT from '@components/tailwind-components/Input/TextareaT';
import LabelT from '@components/tailwind-components/Label/LabelT';
import { SuggestionType } from '@conversed/shared/src/central/types/suggestion-box.type';
import {
  Disclosure,
  DisclosureButton,
  DisclosurePanel,
} from '@headlessui/react';
import { toastErrorTranslated } from '@helpers/toast';
import { useModal } from '@hooks/modal/useModal';
import type React from 'react';
import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSuggestionBoxActions } from '../../../hooks/suggestion-box/useSuggestionBoxActions';
import { useCompanySelector } from '../../../hooks/useCompanySelector';
import { useMainSelector } from '../../../hooks/useMainSelector';

export const SuggestionBoxModal: React.FC = () => {
  const { t } = useTranslation();
  const { hideModal } = useModal();
  const [description, setDescription] = useState('');
  const [frustration, setFrustration] = useState('');
  const [type, setType] = useState<SuggestionType>(SuggestionType.IMPROVEMENT);
  const [descriptionError, setDescriptionError] = useState<boolean>(false);
  const { sendSuggestionBoxEmail } = useSuggestionBoxActions();
  const {
    user: { currentUser },
  } = useMainSelector();
  const { selectedCompany } = useCompanySelector();

  const handleDescriptionChange = useCallback(
    (e: React.ChangeEvent<HTMLTextAreaElement>) => {
      if (descriptionError) {
        setDescriptionError(false);
      }
      setDescription(e.target.value);
    },
    [descriptionError],
  );

  const handleFrustrationChange = useCallback(
    (e: React.ChangeEvent<HTMLTextAreaElement>) => {
      setFrustration(e.target.value);
    },
    [],
  );

  const handleTypeChange = useCallback((newType: SuggestionType) => {
    setType(newType);
  }, []);

  const handleSubmit = useCallback(async () => {
    if (!description.trim()) {
      setDescriptionError(true);
      toastErrorTranslated('suggestion-box.modal.error.description-required');
      return;
    }

    try {
      const userName =
        currentUser?.firstName && currentUser?.lastName
          ? `${currentUser.firstName} ${currentUser.lastName}`
          : currentUser?.email || 'Unkown User';

      await sendSuggestionBoxEmail({
        description: description.trim(),
        frustration: frustration?.trim() || '',
        suggestionType: type,
        userName,
        companyName: selectedCompany?.name || 'Unknown Company',
      });
      hideModal();
    } catch (error) {
      if (error instanceof Error) {
        toastErrorTranslated(error.message);
      }
    }
  }, [
    description,
    frustration,
    type,
    sendSuggestionBoxEmail,
    currentUser,
    selectedCompany,
    hideModal,
  ]);

  const handleCancel = useCallback(() => {
    hideModal();
  }, [hideModal]);

  return (
    <div className='tailwind-modal-form flex-1 min-h-[300px]'>
      <div className='tailwind-modal-body'>
        <div className='suggestion-box-modal__container'>
          {/* Description Section */}
          <div className='suggestion-box-modal__section'>
            <div className='suggestion-box-modal__disclosure'>
              <div className='suggestion-box-modal__disclosure-section'>
                <Disclosure as='div' defaultOpen={true}>
                  {({ open }) => (
                    <>
                      <DisclosureButton className='suggestion-box-modal__disclosure-button'>
                        <MaterialIcon
                          icon={open ? 'expand_less' : 'expand_more'}
                          classes={[
                            'suggestion-box-modal__disclosure-button-icon',
                          ]}
                        />
                        <h3 className='suggestion-box-modal__disclosure-button-text'>
                          {t('suggestion-box.modal.description.label')}
                        </h3>
                      </DisclosureButton>
                      <DisclosurePanel className='flex-grow overflow-y-auto'>
                        <TextareaT
                          field='suggestion-description'
                          parentClassName='suggestion-box-modal__textarea-container'
                          className={`suggestion-box-modal__textarea suggestion-box-modal__textarea--description ${descriptionError ? 'suggestion-box-modal__textarea--mandatory' : ''}`}
                          value={description}
                          onChange={handleDescriptionChange}
                          placeholder={t(
                            'suggestion-box.modal.description.label',
                          )}
                          shouldDynamicHeight={true}
                          rows={3}
                        />
                      </DisclosurePanel>
                    </>
                  )}
                </Disclosure>
              </div>
            </div>
          </div>

          {/* Frustration Section */}
          <div className='suggestion-box-modal__section'>
            <div className='suggestion-box-modal__disclosure'>
              <div className='suggestion-box-modal__disclosure-section'>
                <Disclosure as='div' defaultOpen={true}>
                  {({ open }) => (
                    <>
                      <DisclosureButton className='suggestion-box-modal__disclosure-button'>
                        <MaterialIcon
                          icon={open ? 'expand_less' : 'expand_more'}
                          classes={[
                            'suggestion-box-modal__disclosure-button-icon',
                          ]}
                        />
                        <h3 className='suggestion-box-modal__disclosure-button-text'>
                          {t('suggestion-box.modal.frustration.label')}
                        </h3>
                      </DisclosureButton>
                      <DisclosurePanel className='flex-grow overflow-y-auto'>
                        <TextareaT
                          field='suggestion-frustration'
                          parentClassName='suggestion-box-modal__textarea-container'
                          className='suggestion-box-modal__textarea suggestion-box-modal__textarea--frustration'
                          value={frustration}
                          onChange={handleFrustrationChange}
                          placeholder={t(
                            'suggestion-box.modal.frustration.label',
                          )}
                          shouldDynamicHeight={true}
                          rows={3}
                        />
                      </DisclosurePanel>
                    </>
                  )}
                </Disclosure>
              </div>
            </div>
          </div>

          {/* Suggestion Type Section */}
          <div className='suggestion-box-modal__section suggestion-box-modal__section--type'>
            <LabelT
              text={t('suggestion-box.modal.type.label')}
              className='suggestion-box-modal__section-label'
            />
            <div className='suggestion-box-modal__type-options'>
              <RadioInputT
                id='type-improvement'
                name='suggestion-type'
                label={t('suggestion-box.modal.type.improvement')}
                checked={type === SuggestionType.IMPROVEMENT}
                onChange={() => handleTypeChange(SuggestionType.IMPROVEMENT)}
                labelClassName='text-xs text-gray-800'
                className='my-0'
              />
              <RadioInputT
                id='type-new-feature'
                name='suggestion-type'
                label={t('suggestion-box.modal.type.new-feature')}
                checked={type === SuggestionType.NEW_FEATURE}
                onChange={() => handleTypeChange(SuggestionType.NEW_FEATURE)}
                labelClassName='text-xs text-gray-800'
                className='my-0'
              />
            </div>
          </div>
        </div>
      </div>

      <div className='tailwind-modal-footer'>
        <ButtonT
          onClick={handleCancel}
          type='button'
          styling='cancel'
          size='xs'
          rounding='md'>
          {t('general.buttons.cancel')}
        </ButtonT>
        <ButtonT
          type='button'
          styling='primary'
          size='xs'
          rounding='md'
          onClick={handleSubmit}>
          {t('general.buttons.submit')}
        </ButtonT>
      </div>
    </div>
  );
};

export default SuggestionBoxModal;
